import type { ID } from '@proofroom/schema';
import type { UserRef } from '../api/endpoints';
import { useSession } from '../auth/session';

/**
 * The server resolves every author id it returns into a `UserRef` (id, name, role, no email), so
 * names come straight off the entity and no user directory lookup is needed. `GET /api/users` is
 * admin only and is deliberately not called here.
 *
 * The two labels below are the only cases a name cannot come from the data: an event with no actor
 * at all (the server or the importer acting on its own), and an id the server could not resolve,
 * which means the user has since been deleted. A share-link guest's name comes off the reference
 * like anyone else's (role 'guest'); the reader themselves may be a guest too.
 */
export const UNKNOWN_ACTOR = 'Former member';
export const SYSTEM_ACTOR = 'Proofroom';

export type NameOf = (ref: UserRef | undefined, id?: ID) => string;

/** Whoever is reading: a user or a guest, either way an id and a name */
export type Self = { id: ID; name: string };

export function resolveName(ref: UserRef | undefined, id: ID | undefined, self?: Self | null): string {
  if (ref?.name) return ref.name;
  if (!id) return SYSTEM_ACTOR;
  if (self && id === self.id) return self.name;
  return UNKNOWN_ACTOR;
}

export function usePeople(): { nameOf: NameOf } {
  const { user, guest } = useSession();
  const self = user ?? guest;
  return { nameOf: (ref, id) => resolveName(ref, id ?? ref?.id, self) };
}
